/** Filters for the finance student directory, which reads the privacy-limited
 *  service directory rather than the full student list. */

export const FINANCE_STUDENT_PAGE_SIZES = [25, 50, 100];

export type FinanceStudentFilters = {
  search: string;
  enrolment: keyof typeof FINANCE_ENROLMENT;
  current_class: string;
  gender: "" | "male" | "female";
  residence: "" | "day" | "boarding";
  uses_transport: "" | "true" | "false";
  ordering: keyof typeof FINANCE_STUDENT_ORDERINGS;
};

export const EMPTY_FINANCE_STUDENT_FILTERS: FinanceStudentFilters = {
  search: "",
  enrolment: "in_school",
  current_class: "",
  gender: "",
  residence: "",
  uses_transport: "",
  ordering: "name",
};

// Finance still collects from learners who have left, so it can list them;
// "In school" is where the directory starts, as it always has.
export const FINANCE_ENROLMENT = {
  in_school: ["In school", { status: "active" }],
  suspended: ["Suspended", { status: "suspended" }],
  left: ["Left school", { status__in: "inactive,graduated,transferred,withdrawn,expelled" }],
  all: ["All", {}],
} as const;

// Every ordering ends on id so paging is stable when names repeat.
export const FINANCE_STUDENT_ORDERINGS = {
  name: ["Name A–Z", "user__first_name,user__last_name,id"],
  "-name": ["Name Z–A", "-user__first_name,-user__last_name,-id"],
  class: ["Class", "current_class__level_order,current_class__level,current_class__section,user__first_name,user__last_name,id"],
  student_id: ["Student ID", "student_id,id"],
} as const;

export const STUDENT_ENROLMENT_DOTS: Record<string, string> = {
  active: "bg-green-500",
  suspended: "bg-amber-500",
  inactive: "bg-gray-400",
  graduated: "bg-blue-400",
  transferred: "bg-gray-400",
  withdrawn: "bg-gray-400",
  expelled: "bg-red-700",
};

/** The query the service directory expects for these filters. */
export function financeStudentParams(filters: FinanceStudentFilters) {
  const params: Record<string, string | number | boolean> = {
    include_left: true,
    ordering: FINANCE_STUDENT_ORDERINGS[filters.ordering][1],
    ...FINANCE_ENROLMENT[filters.enrolment][1],
  };
  const search = filters.search.trim();
  if (search) params.search = search;
  if (filters.current_class) params.current_class = Number(filters.current_class);
  if (filters.gender) params.gender = filters.gender;
  if (filters.residence) params.residence = filters.residence;
  if (filters.uses_transport) params.uses_transport = filters.uses_transport === "true";
  return params;
}

/** How many filters narrow the list; sorting only reorders it, and "In school" is the starting view. */
export function activeFinanceStudentFilterCount(filters: FinanceStudentFilters) {
  return [
    filters.search.trim(),
    filters.enrolment !== "in_school",
    filters.current_class,
    filters.gender,
    filters.residence,
    filters.uses_transport,
  ].filter(Boolean).length;
}

/** Up to two initials for the avatar beside a learner's name. */
export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 2)
    .join("");
